import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { UserType } from "../../../../constants";
import { CursorChangeProps, SortCriteria } from "../../../../models";
import { searchCollaborators, SearchCollaboratorsResponse } from "../../../../service/query";
import { BaseResponseWithSingle, ResultData } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { flattenObject } from "../../../../utils";
import { getCollaboratorRoute, getOperationsCompaniesRoute } from "../../../../utils/routes";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridCellFormatter,
  DataGridColumnDefinitionWithCustomCellFormatter,
} from "../../../../widget";

interface UseListReturnData {
  dataGridColumns: DataGridColumnDefinitionWithCustomCellFormatter<
    DataGridButtonLinkCellFormatterData | string | null | undefined
  >[];
  isLoading: boolean;
  showAddEditCompanyModal: boolean;
  showCompanyDetailsModal: boolean;
  selectedCompanyUuid: string | null;
  refreshCompaniesGrid: boolean;
  onDataGridChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }>;
  onAddCompanyClick: () => void;
  onCloseAddEditCompanyModal: () => void;
  onCloseCompanyDetailsModal: () => void;
  onCompanySaved: () => void;
  onEditCompanyClick: () => void;
  onCompanyDeleted: () => void;
  fetchCompanyByUuid: (companyUuid: string) => Promise<SearchCollaboratorsResponse["results"][number] | null>;
  companyFormMode: "add" | "edit";
  currentUserType: UserType;
}

interface ResultType {
  resultData: ResultData[];
  paging: {
    startCursor: string;
    endCursor: string;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    pageSize: number;
    totalCount: number;
  };
}

export const useList = (): UseListReturnData => {
  const { currentUserType } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [showAddEditCompanyModal, setShowAddEditCompanyModal] = useState(false);
  const [refreshCompaniesGrid, setRefreshCompaniesGrid] = useState(false);
  const [companyFormMode, setCompanyFormMode] = useState<"add" | "edit">("add");
  const selectedCompanyUuid = searchParams.get("companyUuid");
  const showCompanyDetailsModal = useMemo(
    () => Boolean(selectedCompanyUuid) && !showAddEditCompanyModal,
    [selectedCompanyUuid, showAddEditCompanyModal]
  );
  const companiesRoute = getOperationsCompaniesRoute(currentUserType);
  const navigateToCompanies = useCallback(
    (replace = false): void => {
      navigate(companiesRoute, { replace, state: location.state });
    },
    [companiesRoute, location.state, navigate]
  );
  const navigateToCompany = useCallback(
    (companyUuid: string): void => {
      navigate(getCollaboratorRoute(companyUuid, currentUserType), { state: location.state });
    },
    [currentUserType, location.state, navigate]
  );

  const companyNameFormatter: DataGridCellFormatter<DataGridButtonLinkCellFormatterData | string | null | undefined> = (
    columnDefinition
  ) => {
    return (value) => {
      if (value === undefined || value === null) return <div />;
      if (typeof value === "string") {
        return (
          <div className={`DataGridLink ${columnDefinition.alignment || ""}`}>
            <span className="body2">
              <b>{value}</b>
            </span>
          </div>
        );
      }

      const { action, text } = value;

      return (
        <div className={`DataGridLink ${columnDefinition.alignment || ""}`}>
          <span className="body2">
            <button
              type="button"
              className="BtnLink"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                action();
              }}
            >
              {text}
            </button>
          </span>
        </div>
      );
    };
  };

  const dataGridColumns: DataGridColumnDefinitionWithCustomCellFormatter<
    DataGridButtonLinkCellFormatterData | string | null | undefined
  >[] = [
    {
      key: "name",
      name: "Company",
      dataType: "string",
      formatter: "custom",
      customCellFormatter: companyNameFormatter,
    },
    { key: "collaboratorType", name: "Type", dataType: "string" },
    { key: "addressRegion", name: "Region", dataType: "string" },
    { key: "email", name: "Email", dataType: "string" },
  ];
  const defaultSort: SortCriteria[] = [{ key: "results.name", direction: "asc" }];
  const pageSize = 10;
  const onCompanyDetails = useCallback(
    (company: SearchCollaboratorsResponse["results"][number]) => {
      setShowAddEditCompanyModal(false);
      navigateToCompany(company.uuid);
    },
    [navigateToCompany]
  );

  const formatData = useCallback(
    (responseData: SearchCollaboratorsResponse | undefined): ResultData[] =>
      responseData?.results?.map((d) => {
        const result = flattenObject(d);

        result.name = {
          text: d.name,
          action: () => onCompanyDetails(d),
        } as DataGridButtonLinkCellFormatterData;

        return result;
      }) || [],
    [onCompanyDetails]
  );

  const onDataGridChange = async ({ paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    await searchCollaborators({
      paging: {
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
        limit: pageSize,
      },
      /* eslint-disable @typescript-eslint/no-explicit-any */
      sort: ((sorting?.length ? sorting : defaultSort) as SortCriteria[]).map((s: { key: any; direction: any }) => ({
        key: s.key as any,
        direction: s.direction,
      })),
      /* eslint-enable */
    })
      .then((response: BaseResponseWithSingle<SearchCollaboratorsResponse>) => {
        data = {
          resultData: formatData(response.data),
          paging: {
            startCursor: response.data?.paging.startCursor || "",
            endCursor: response.data?.paging.endCursor || "",
            pageSize,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setIsLoading(false);
      });

    return data;
  };

  const onAddCompanyClick = (): void => {
    setCompanyFormMode("add");
    navigateToCompanies();
    setShowAddEditCompanyModal(true);
  };
  const onCloseAddEditCompanyModal = (): void => setShowAddEditCompanyModal(false);
  const onEditCompanyClick = (): void => {
    if (!selectedCompanyUuid) return;
    setCompanyFormMode("edit");
    setShowAddEditCompanyModal(true);
  };
  const onCompanySaved = (): void => {
    setRefreshCompaniesGrid((prev) => !prev);
    setShowAddEditCompanyModal(false);
    if (companyFormMode === "add") {
      navigateToCompanies(true);
    }
  };
  const onCloseCompanyDetailsModal = (): void => {
    navigateToCompanies(true);
  };
  const onCompanyDeleted = (): void => {
    setRefreshCompaniesGrid((prev) => !prev);
  };
  const fetchCompanyByUuid = useCallback(
    async (companyUuid: string): Promise<SearchCollaboratorsResponse["results"][number] | null> => {
      const response = await searchCollaborators({
        paging: {
          beforeCursor: null,
          afterCursor: null,
          limit: 1,
        },
        filter: {
          results: {
            uuid: {
              operator: "eq",
              value: companyUuid,
            },
          },
        },
      });

      if (response.data?.results?.length) return response.data.results[0];
      return null;
    },
    []
  );
  return {
    dataGridColumns,
    isLoading,
    showAddEditCompanyModal,
    showCompanyDetailsModal,
    selectedCompanyUuid,
    refreshCompaniesGrid,
    onAddCompanyClick,
    onCloseAddEditCompanyModal,
    onCloseCompanyDetailsModal,
    onCompanySaved,
    onEditCompanyClick,
    onCompanyDeleted,
    fetchCompanyByUuid,
    companyFormMode,
    onDataGridChange,
    currentUserType,
  };
};
